/* eslint-disable eag/no-index -- Pre-existing violation */
import {Card} from 'platform/components';
import {Box, Grid, Hide, Show, VStack} from 'platform/foundation';

import {useEffect} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {OfferResponseBody} from '@dms/api/finance';
import {useGetBusinessCaseQuery, useGetVehicleQuery, useGetVehicleV2Query} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CommentsWidget,
  DmsDataGrid,
  EMPTY_PLACEHOLDER,
  PriceReportWidget,
  refreshDmsDatagrid,
  usePermissions,
  usePricePermissions,
} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {getPurchaseVehicleIdFromBusinessCase} from '../../../../utils/getPurchaseVehicleIdFromBusinessCase';
import {CentralizedPricingWidget} from './CentralizedPricing/index';
import {PricingWidget} from './SimplifiedPricing/index';

interface PricingTabProps {
  isCentralizedPricingEnabled?: boolean;
  isBrokerage?: boolean;
  offer: OfferResponseBody;
}

function PricingTab(props: PricingTabProps) {
  const {id: businessCaseId} = useRequiredParams();
  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});
  const purchaseVehicleId = getPurchaseVehicleIdFromBusinessCase(businessCase);

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: purchaseVehicleId ?? '',
    },
    {skip: isNil(purchaseVehicleId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: businessCaseId,
    },
    {skip: isNil(businessCaseId)}
  );

  const {data: vehicle} = useGetVehicleV2Query(
    {vehicleId: purchaseVehicleId!},
    {skip: !purchaseVehicleId}
  );

  const {data: vehicleV1} = useGetVehicleQuery(
    {vehicleId: purchaseVehicleId!},
    {skip: !purchaseVehicleId}
  );

  const refreshDataGrid = () => refreshDmsDatagrid('business_case_price_snapshot');
  const [
    hasVehicleCommentReadPermission,
    hasVehicleCommentAddPermission,
    hasVehicleCommentDeletePermission,
    hasBusinessCasePurchasePricePermission,
    canReadPriceReportWidget,
  ] = usePermissions({
    permissionKeys: [
      'vehicleCommentRead',
      'vehicleCommentAdd',
      'vehicleCommentDelete',
      'businessCasePurchasePrice',
      'createVehiclePriceReport',
    ],
    scopes: {
      vehicleCommentAdd: {
        participation: vehicleParticipation,
        branchId: vehicleV1?.branchId,
      },
      vehicleCommentRead: {
        participation: vehicleParticipation,
        branchId: vehicleV1?.branchId,
      },
      vehicleCommentDelete: {
        participation: vehicleParticipation,
        branchId: vehicleV1?.branchId,
      },
      businessCasePurchasePrice: {
        participation: businessCaseParticipation,
        branchId: vehicleV1?.branchId,
      },
    },
  });

  const {canReadVehiclePurchasePrice} = usePricePermissions({
    vehicleRecordId: purchaseVehicleId,
    businessCaseRecordId: null,
  });

  const {requestId} = useGetSaleVehicleQuery(
    {vehicleId: purchaseVehicleId ?? ''},
    {skip: isNil(purchaseVehicleId)}
  );

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {vehicleId: purchaseVehicleId}]);

  useEffect(
    function refreshDataGridOnRequestChange() {
      refreshDataGrid();
    },
    // eslint-disable-next-line react/exhaustive-deps
    [requestId]
  );

  return (
    <Grid columns={[1, 2]}>
      <VStack spacing={4}>
        {props.isCentralizedPricingEnabled && !props.isBrokerage ? (
          <CentralizedPricingWidget />
        ) : (
          <PricingWidget
            isReadonly={!hasBusinessCasePurchasePricePermission}
            isBrokerage={props.isBrokerage}
            offer={props.offer}
          />
        )}

        <Show when={canReadPriceReportWidget}>
          <PriceReportWidget
            resourceId={purchaseVehicleId}
            vehicleType={vehicle?.type}
            context="vehicle"
            data-testid={testIds.businessCase.buying('priceReportWidget')}
          />
        </Show>
      </VStack>
      <Hide when={isNilOrEmpty(purchaseVehicleId)}>
        <VStack spacing={4}>
          <Show
            when={
              businessCase?.businessCaseInternalType === 'BUYING' && canReadVehiclePurchasePrice
            }
          >
            <Card title={i18n.t('entity.vehicle.labels.pricingHistory')} variant="inlineWhite">
              <Box minHeight={40}>
                <DmsDataGrid
                  maximization={{
                    enabled: true,
                    title: `${i18n.t('entity.customerMessage.labels.contextResource.BUSINESS_CASE')}: ${businessCase?.code ?? EMPTY_PLACEHOLDER}`,
                  }}
                  gridCode="business_case_price_snapshot"
                  autoHeight
                  queryModifier={queryModifier}
                  data-testid={testIds.businessCase.buying('business_case_price_snapshot')}
                />
              </Box>
            </Card>
          </Show>
          <Show when={hasVehicleCommentReadPermission}>
            <CommentsWidget
              cardVariant="inlineWhite"
              title={i18n.t('entity.vehicle.labels.vehicleComments')}
              resourceId={EntityResourceIds.vehicle}
              recordId={purchaseVehicleId!}
              isAddDisabled={!hasVehicleCommentAddPermission}
              isDeleteDisabled={!hasVehicleCommentDeletePermission}
              data-testid={testIds.businessCase.buying('commentsWidget')}
            />
          </Show>
        </VStack>
      </Hide>
    </Grid>
  );
}

export const BusinessCasePurchaseVehiclePricing = PricingTab;
